/** @jsxRuntime classic */
/** @jsx jsx */
import type { FC, FocusEvent } from 'react';
import { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CrossIcon from '@atlaskit/icon/core/cross';
import DeleteIcon from '@atlaskit/icon/core/delete';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StrokeWeightLargeIcon from '@atlaskit/icon/core/stroke-weight-large';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';

import { fg } from '@confluence/feature-gating';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';

import type { RecentWorkListItemLayoutProps } from '../../layouts/RecentWorkListItemLayout.tsx';
import { RecentWorkListItemLayout } from '../../layouts/RecentWorkListItemLayout.tsx';
import { recentWorkMessages } from '../../recentWorkMessages.ts';
import { getContentIcon, getContentTypeMessage } from '../recentWorkContent.tsx';

// Non-final: the hover actions are still being designed behind cc_ai_agentic_sessions_card_actions.
const messages = defineMessages({
	continueSession: {
		id: 'entity-view.recent-work.agent-session.continue.non-final',
		defaultMessage: 'Continue',
		description:
			'Call to action shown when hovering an agent session in the recent work list. Selecting the session resumes it.',
	},
	moreActions: {
		id: 'entity-view.recent-work.agent-session.more-actions.non-final',
		defaultMessage: 'More actions for {title}',
		description:
			'Accessible label for the button that opens the actions menu of an agent session. {title} is the session title.',
	},
	dismissSession: {
		id: 'entity-view.recent-work.agent-session.dismiss.non-final',
		defaultMessage: 'Dismiss',
		description: 'Menu item that hides an agent session from the recent work list.',
	},
	deleteSession: {
		id: 'entity-view.recent-work.agent-session.delete.non-final',
		defaultMessage: 'Delete',
		description: 'Menu item that permanently deletes an agent session.',
	},
});

const styles = cssMap({
	title: {
		flexGrow: 1,
		maxWidth: '100%',
		minWidth: 0,
	},
	titleText: {
		display: 'block',
		overflow: 'hidden',
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		width: '100%',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	metadata: {
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		whiteSpace: 'nowrap',
	},
	separator: {
		flexShrink: 0,
		marginInlineStart: token('space.050'),
		marginInlineEnd: token('space.050'),
	},
	spaceLinkWrapper: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	agentMetadata: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		columnGap: token('space.050'),
	},
	agentPicture: {
		flexShrink: 0,
		objectFit: 'cover',
		borderRadius: token('radius.full'),
	},
	statusIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '24px',
		height: '24px',
	},
	sessionLocation: {
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	status: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexShrink: 0,
		columnGap: token('space.050'),
		font: token('font.body.small'),
		minWidth: '112px',
		minHeight: '24px',
	},
	workingStatus: {
		color: token('color.text.subtle'),
	},
	needsInputStatus: {
		color: token('color.text.subtle'),
	},
	finishedStatus: {
		color: token('color.text.success'),
	},
	// Status and hover actions share one grid cell so swapping them does not shift the row.
	actionsStack: {
		display: 'grid',
		alignItems: 'center',
		justifyItems: 'end',
	},
	actionsStackItem: {
		gridColumnStart: 1,
		gridRowStart: 1,
	},
	hidden: {
		visibility: 'hidden',
	},
	hoverActions: {
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.100'),
	},
	continue: {
		display: 'flex',
		alignItems: 'center',
		columnGap: token('space.050'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
	},
	menuTrigger: {
		// The row's actions area passes clicks through to the row link; the menu opts back in.
		pointerEvents: 'auto',
	},
	menuContent: {
		// Fixed width from the Figma actions popup.
		width: '220px',
	},
	spaceLink: {
		overflow: 'hidden',
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		textOverflow: 'ellipsis',
		'&:visited': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
		'&:hover': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
	},
});

export type AgentSessionCardStatus = 'working' | 'needs-input' | 'finished';

const AgentSessionStatus = ({ status }: { status: AgentSessionCardStatus }): JSX.Element => {
	switch (status) {
		case 'working':
			return (
				<div css={[styles.status, styles.workingStatus]} data-testid="agent-session-working-status">
					<FormattedMessage {...recentWorkMessages.working} />
					<EllipsesAnimation />
				</div>
			);
		case 'finished':
			return (
				<div
					css={[styles.status, styles.finishedStatus]}
					data-testid="agent-session-finished-status"
				>
					<FormattedMessage {...recentWorkMessages.finished} />
					<Box xcss={styles.statusIcon}>
						<StatusSuccessIcon
							label=""
							size="medium"
							color={token('color.icon.success')}
							testId="agent-session-finished-icon"
						/>
					</Box>
				</div>
			);
		case 'needs-input':
		default:
			return (
				<div
					css={[styles.status, styles.needsInputStatus]}
					data-testid="agent-session-needs-input-status"
				>
					<FormattedMessage {...recentWorkMessages.needsInput} />
					<Box xcss={styles.statusIcon}>
						<StrokeWeightLargeIcon
							label=""
							size="small"
							color={token('color.icon.information')}
							testId="agent-session-needs-input-icon"
						/>
					</Box>
				</div>
			);
	}
};

export type AgentSessionListItemViewProps = Pick<RecentWorkListItemLayoutProps, 'onClick'> & {
	title?: string | null;
	url?: string;
	agentName?: string | null;
	agentPicture?: string | null;
	contentType?: string;
	contentSubType?: string | null;
	space?: { name?: string | null; spaceKey?: string | null } | null;
	status?: AgentSessionCardStatus;
	onDismiss?: () => void;
	onDelete?: () => void;
};

type AgentSessionHoverActionsProps = {
	title: string;
	onDismiss?: () => void;
	onDelete?: () => void;
	onMenuOpenChange: (isOpen: boolean) => void;
};

const AgentSessionHoverActions = ({
	title,
	onDismiss,
	onDelete,
	onMenuOpenChange,
}: AgentSessionHoverActionsProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.hoverActions} data-testid="agent-session-hover-actions">
			<span css={styles.continue} aria-hidden="true" data-testid="agent-session-continue">
				<FormattedMessage {...messages.continueSession} />
				<ArrowRightIcon label="" size="small" color={token('color.icon.subtle')} />
			</span>
			<div css={styles.menuTrigger}>
				<DropdownMenu
					placement="bottom-start"
					shouldFlip
					onOpenChange={({ isOpen }) => onMenuOpenChange(isOpen)}
					testId="agent-session-actions-menu"
					trigger={({ triggerRef, ...triggerProps }) => (
						<IconButton
							{...triggerProps}
							ref={triggerRef}
							icon={ShowMoreHorizontalIcon}
							label={formatMessage(messages.moreActions, { title })}
							appearance="subtle"
							spacing="compact"
						/>
					)}
				>
					<div css={styles.menuContent}>
						<DropdownItem elemBefore={<CrossIcon label="" />} onClick={onDismiss}>
							<FormattedMessage {...messages.dismissSession} />
						</DropdownItem>
						<DropdownItem
							elemBefore={<DeleteIcon label="" color={token('color.icon.danger')} />}
							onClick={onDelete}
						>
							<Text color="color.text.danger">
								<FormattedMessage {...messages.deleteSession} />
							</Text>
						</DropdownItem>
					</div>
				</DropdownMenu>
			</div>
		</div>
	);
};

export const AgentSessionListItemView: FC<AgentSessionListItemViewProps> = ({
	title,
	url,
	onClick,
	agentName,
	agentPicture,
	contentType,
	contentSubType,
	space,
	status,
	onDismiss,
	onDelete,
}) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const [hasFocusWithin, setHasFocusWithin] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const isActive = isHovered || hasFocusWithin || isMenuOpen;

	const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			setHasFocusWithin(false);
		}
	}, []);
	const titleContent = title || formatMessage(recentWorkMessages.agentSession);
	const resolvedAgentName = agentName || formatMessage(recentWorkMessages.rovoAgent);
	const contentTypeMessage =
		getContentTypeMessage(contentType, contentSubType) || recentWorkMessages.chat;
	const primary = (
		<Tooltip content={titleContent} position="bottom">
			{(tooltipProps) => (
				<Box xcss={styles.title} testId="agent-session-card-title" {...tooltipProps}>
					<span css={styles.titleText}>{titleContent}</span>
				</Box>
			)}
		</Tooltip>
	);
	const secondary = (
		<div css={styles.metadata}>
			<span css={styles.agentMetadata}>
				{agentPicture && (
					<img
						css={styles.agentPicture}
						src={agentPicture}
						width={12}
						height={12}
						alt=""
						data-testid="agent-session-agent-picture"
					/>
				)}
				<span data-testid="agent-session-agent-name">{resolvedAgentName}</span>
			</span>
			<Box as="span" xcss={styles.separator} aria-hidden="true">
				·
			</Box>
			<FormattedMessage {...contentTypeMessage} />
			{space?.name && (
				<span css={styles.sessionLocation}>
					<Box as="span" xcss={styles.separator} aria-hidden="true">
						·
					</Box>
					<Box as="span" xcss={styles.spaceLinkWrapper}>
						<SpacesIcon
							label=""
							size="small"
							color={token('color.icon.subtle')}
							testId="agent-session-space-icon"
						/>
						{space.spaceKey ? (
							<Anchor
								xcss={styles.spaceLink}
								href={SPACE_OVERVIEW.toUrl({ spaceKey: space.spaceKey })}
							>
								{space.name}
							</Anchor>
						) : (
							space.name
						)}
					</Box>
				</span>
			)}
		</div>
	);
	const row = (
		<RecentWorkListItemLayout
			url={url}
			onClick={onClick}
			ariaLabel={titleContent}
			testId="agent-session-list-item"
			icon={
				contentType ? (
					getContentIcon(contentType, contentSubType)
				) : (
					<RovoChatIcon
						label=""
						color={token('color.icon.subtle')}
						testId="agent-session-chat-icon"
					/>
				)
			}
			secondary={secondary}
			actions={
				// Replaces the status with Continue and an actions menu while the row is hovered, focused,
				// or its menu is open. Continue is visual only: selecting it resumes through the row link.
				// Dismiss and Delete are not wired up until the backend mutations are available.
				fg('cc_ai_agentic_sessions_card_actions') ? (
					<div css={styles.actionsStack}>
						{status && (
							<div css={[styles.actionsStackItem, isActive && styles.hidden]}>
								<AgentSessionStatus status={status} />
							</div>
						)}
						<div css={[styles.actionsStackItem, !isActive && styles.hidden]}>
							<AgentSessionHoverActions
								title={titleContent}
								onDismiss={onDismiss}
								onDelete={onDelete}
								onMenuOpenChange={setIsMenuOpen}
							/>
						</div>
					</div>
				) : status ? (
					<AgentSessionStatus status={status} />
				) : undefined
			}
		>
			{primary}
		</RecentWorkListItemLayout>
	);

	const content = url ? (
		<HoverPreloader
			name="agent-session-list-item"
			preloadUrl={url}
			preloadOptions={PreloadingPresets.onlyJS}
		>
			{row}
		</HoverPreloader>
	) : (
		row
	);

	if (!fg('cc_ai_agentic_sessions_card_actions')) {
		return content;
	}

	// Wraps outside HoverPreloader so its cloned child (and preload behavior) is unchanged.
	return (
		<div
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setHasFocusWithin(true)}
			onBlur={handleBlur}
		>
			{content}
		</div>
	);
};
